/**
 * Portal-Monatskalender — Kacheln am Tag, Pane-Fit.
 * Tag-Klick öffnet die Tagesansicht (Liste), nicht nur den Create-Dialog.
 * Anlegen: Plus oder leerer Stundenslot → Popup (POPOVER_DISMISS_OUTSIDE).
 * Fristen aus `portal_fristen` nur anzeigen (Akt-Hub bleibt CRUD-Owner).
 * Klasse KALENDER_TILE_SEAT_OR_SKIP + KALENDER_DAY_OPEN_LIST + KALENDER_SEARCH_CLICK
 * + POPOVER_DISMISS_OUTSIDE.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { portalKalenderSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import { PortalShortcutHint } from '../PortalShortcutHint';
import { usePortalShortcutAria } from '../portalShortcutContext';
import { listKanzleiSeatsForAktePicker } from '../portalAkten';
import type { KanzleiSeatForAktePicker } from '../portalAktenTypes';
import { placeSharePopover, portalShareSeatLabel } from '../portalDocumentsPrivacy';
import { usePopoverDismissOutside } from '../usePopoverDismissOutside';
import { GLOCKE_SPRUNG_EVENT, peekGlockeSprung, type GlockeSprung } from '../portalGlockeSprung';
import { listPortalFristenForAkte } from '../verfahrensmotor/portalFristen';
import type { PortalFristRecord } from '../verfahrensmotor/fristApprovalTypes';
import {
  CALENDAR_SEARCH_EMPTY_LABEL,
  PORTAL_KALENDER_DAY_CLASS,
  PORTAL_KALENDER_SEARCH_CLASS,
  WEEKDAY_LABELS_DE,
  CALENDAR_HOUR_MIN_PX,
  CALENDAR_QUARTER_MIN_PX,
  DAY_HOUR_END,
  DAY_HOUR_START,
  addCalendarDays,
  addCalendarMonths,
  calendarDropTarget,
  calendarEventBox,
  calendarTimeRange,
  calendarWeekIsos,
  calendarReminderMarkVisible,
  readCalendarReminderOffset,
  writeCalendarReminderOffset,
  type CalendarReminderMinutes,
  isValidCalendarStoredTime,
  resizeCalendarEdge,
  applyCalendarSearchDateJump,
  applyCalendarSearchHitClick,
  applyMonthDayClick,
  buildDayAgenda,
  assignCalendarEventLanes,
  buildDayHourRows,
  shiftCalendarTime,
  buildDayTiles,
  buildMonthGrid,
  eventAffectsMultipleSeats,
  eventStartsInHour,
  eventsOnDate,
  formatHourLabel,
  formatLongDeDate,
  formatMonthHeading,
  fristLabelOnDate,
  fristTouchesIsoDate,
  isoDateLocal,
  mondayIndexFromJsDay,
  DAY_POPOVER_EST_HEIGHT_PX,
  DAY_POPOVER_WIDTH_PX,
  parseFlexibleDate,
  parseFlexibleTime,
  parseIsoDateLocal,
  sanitizeKalenderSeatId,
  searchCalendarEvents,
  type CalendarSearchHit,
  type CalendarSurface,
  type PortalCalendarEvent,
} from '../calendar/portalCalendar';
import {
  deleteCalendarEventFromPb,
  loadCalendarEventsFromPb,
  saveCalendarEventToPb,
} from '../calendar/portalCalendarPersist';
import { noteSitzGlockePortalAction, sitzGlockeActorFromSeat } from '../sitzGlockeStore';

export interface KalenderAkteOption {
  id: string;
  title: string;
}

export interface KalenderViewProps {
  customerId: string;
  kanzleiId?: string;
  authToken?: string | null;
  akten?: KalenderAkteOption[];
  defaultAkteId?: string | null;
}

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste. */
const KALENDER_GROUND = 'bg-[#07070a]';
const KALENDER_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const KALENDER_BTN =
  'portal-kalender-toolbar inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const KALENDER_ICON =
  'portal-kalender-toolbar inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const KALENDER_VIEW =
  'portal-kalender-toolbar inline-flex shrink-0 items-center rounded-md border border-violet-400/40 bg-violet-600/30 px-2 py-1 text-[11px] font-semibold text-violet-200 hover:bg-violet-600/55';
const KALENDER_FIELD =
  'w-full rounded-md border border-violet-400/30 bg-[#07070a] text-sm text-slate-100 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none [color-scheme:dark]';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur wo der Termin-Dialog auf- und zuklappt.
 */
function KalenderCube({
  open,
  front,
  back,
}: {
  open: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

function calendarTimeLabel(time: string | undefined): string {
  const raw = String(time || '').trim();
  const range = /^(\d{2}:\d{2})-(\d{2}:\d{2})$/.exec(raw);
  return range ? `${range[1]}–${range[2]}` : raw;
}

function timedEventFrame(
  time: string | undefined,
  lane: number,
  laneCount: number,
  drag: { mode?: 'move' | 'start' | 'end'; range?: string; translateX: number; translateY: number } | null,
): React.CSSProperties {
  const shown = drag && drag.mode && drag.mode !== 'move' && drag.range ? drag.range : time;
  const box = calendarEventBox(shown);
  const widthPct = 100 / Math.max(1, laneCount);
  const moving = drag?.mode === 'move';
  return {
    position: 'absolute',
    top: `${box.topPct}%`,
    height: `${box.heightPct}%`,
    minHeight: CALENDAR_QUARTER_MIN_PX,
    left: `calc(${lane * widthPct}% + 1px)`,
    width: `calc(${widthPct}% - 2px)`,
    zIndex: drag ? 2 : 1,
    transform: moving ? `translate(${drag.translateX}px, ${drag.translateY}px)` : undefined,
  };
}

function KalenderTimedEvent({
  ev,
  lane,
  laneCount,
  multi,
  reminderOn,
  dragging,
  onBegin,
  onMove,
  onEnd,
}: {
  ev: PortalCalendarEvent;
  lane: number;
  laneCount: number;
  multi: boolean;
  reminderOn: boolean;
  dragging: {
    mode: 'move' | 'start' | 'end';
    time: string;
    range: string;
    translateX: number;
    translateY: number;
  } | null;
  onBegin: (ev: PortalCalendarEvent, event: React.PointerEvent<HTMLElement>, mode: 'move' | 'start' | 'end') => void;
  onMove: (ev: PortalCalendarEvent, event: React.PointerEvent<HTMLElement>) => void;
  onEnd: (ev: PortalCalendarEvent, event: React.PointerEvent<HTMLElement>) => void;
}) {
  const label = dragging
    ? dragging.mode === 'move'
      ? dragging.time
      : calendarTimeLabel(dragging.range || ev.time)
    : calendarTimeLabel(ev.time);
  return (
    <div
      data-kalender-event-frame=""
      className={dragging ? 'portal-kalender-event-dragging' : ''}
      style={timedEventFrame(ev.time, lane, laneCount, dragging)}
    >
      <div
        className="portal-kalender-edge portal-kalender-edge-start"
        data-testid="portal-kalender-edge-start"
        onPointerDown={(event) => onBegin(ev, event, 'start')}
        onPointerMove={(event) => onMove(ev, event)}
        onPointerUp={(event) => onEnd(ev, event)}
      />
      <button
        type="button"
        data-testid="portal-kalender-day-entry"
        data-kalender-day-kind="event"
        data-kalender-lane={lane}
        onPointerDown={(event) => onBegin(ev, event, 'move')}
        onPointerMove={(event) => onMove(ev, event)}
        onPointerUp={(event) => onEnd(ev, event)}
        className={`portal-kalender-event portal-kalender-event-hover absolute inset-0 text-left rounded-md border px-1 py-0.5 cursor-grab overflow-visible ${tileClass('event', multi)}`}
      >
        <p className="text-[10px] text-slate-400 leading-tight">
          {label}
          {reminderOn ? (
            <Bell
              className="portal-kalender-reminder-mark inline-block w-2.5 h-2.5 ml-1 align-[-1px]"
              aria-hidden
            />
          ) : null}
          {multi ? ' · mehrere Sitze' : ''}
        </p>
        <p className="portal-kalender-event-title text-sm text-slate-100 leading-tight">{ev.title}</p>
      </button>
      <div
        className="portal-kalender-edge portal-kalender-edge-end"
        data-testid="portal-kalender-edge-end"
        onPointerDown={(event) => onBegin(ev, event, 'end')}
        onPointerMove={(event) => onMove(ev, event)}
        onPointerUp={(event) => onEnd(ev, event)}
      />
    </div>
  );
}

function tileClass(kind: 'event' | 'frist', multiSeat?: boolean): string {
  if (kind === 'frist') {
    return 'bg-amber-500/15 text-amber-100 border-amber-400/25';
  }
  if (multiSeat) {
    return 'bg-violet-500/20 text-violet-100 border-violet-400/30';
  }
  return 'bg-[#07070a] text-violet-100 border-violet-400/40';
}

export const KalenderView: React.FC<KalenderViewProps> = ({
  customerId,
  kanzleiId = '',
  authToken,
  akten = [],
  defaultAkteId = null,
}) => {
  const neuShortcut = usePortalShortcutAria('neu');
  const appearance = usePortalAppearance();
  const kalenderLight = portalKalenderSurfaceClass(appearance);
  const today = useMemo(() => new Date(), []);
  const todayIso = isoDateLocal(today);
  const [cursor, setCursor] = useState(() => ({
    year: today.getFullYear(),
    monthIndex: today.getMonth(),
  }));
  const [surface, setSurface] = useState<CalendarSurface>('month');
  const [selectedIso, setSelectedIso] = useState(todayIso);
  const [events, setEvents] = useState<PortalCalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [fristen, setFristen] = useState<PortalFristRecord[]>([]);
  const [fristenLoading, setFristenLoading] = useState(false);
  const [seats, setSeats] = useState<KanzleiSeatForAktePicker[]>([]);
  const [title, setTitle] = useState('');
  const [dateText, setDateText] = useState(todayIso);
  const [timeText, setTimeText] = useState('');
  const [reminderMin, setReminderMin] = useState<CalendarReminderMinutes>(30);
  const [akteId, setAkteId] = useState(defaultAkteId || '');
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [formHint, setFormHint] = useState('');
  const [loadError, setLoadError] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ left: 24, top: 96 });
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const surfaceRef = useRef<HTMLElement | null>(null);
  const hoursRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    moved: boolean;
    mode: 'move' | 'start' | 'end';
    previewTime: string | null;
    previewDate: string | null;
    previewRange: string | null;
    originLeft: number;
    originTop: number;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    id: string;
    mode: 'move' | 'start' | 'end';
    time: string;
    date: string;
    range: string;
    translateX: number;
    translateY: number;
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const createTriggerRef = useRef<HTMLButtonElement | null>(null);

  const writeCtx = useMemo(
    () => ({
      customerId,
      kanzleiId: sanitizeKalenderSeatId(kanzleiId),
      visibleAkteIds: akten.map((a) => a.id),
      seatIds: seats.map((s) => s.id),
    }),
    [akten, customerId, kanzleiId, seats],
  );

  const closePopover = useCallback(() => {
    setPopoverOpen(false);
    setEditingId(null);
    setFormError('');
  }, []);

  usePopoverDismissOutside({
    open: popoverOpen,
    onClose: closePopover,
    rootRef: popoverRef,
    ignoreCloseRef: createTriggerRef,
    closeOnScroll: false,
  });

  const refreshEvents = useCallback(async () => {
    const kid = sanitizeKalenderSeatId(kanzleiId);
    if (!customerId || !kid) {
      setEvents([]);
      setLoadError('Kalender braucht eine Kanzlei — ohne Kanzlei keine Termine.');
      return;
    }
    setEventsLoading(true);
    try {
      const rows = await loadCalendarEventsFromPb({
        customerId,
        kanzleiId: kid,
        authToken,
      });
      setEvents(rows);
      setLoadError('');
    } catch (err) {
      setEvents([]);
      setLoadError(err instanceof Error ? err.message : 'Termine konnten nicht geladen werden.');
    } finally {
      setEventsLoading(false);
    }
  }, [authToken, customerId, kanzleiId]);

  useEffect(() => {
    void refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    const token = String(authToken || '').trim();
    if (!customerId || !token) {
      setFristen([]);
      return;
    }
    let cancelled = false;
    setFristenLoading(true);
    void listPortalFristenForAkte(customerId, token).then((rows) => {
      if (!cancelled) {
        setFristen(rows);
        setFristenLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authToken, customerId]);

  useEffect(() => {
    const token = String(authToken || '').trim();
    const kid = sanitizeKalenderSeatId(kanzleiId);
    if (!token || !kid) {
      setSeats([]);
      return;
    }
    let cancelled = false;
    void listKanzleiSeatsForAktePicker(token, kid).then((rows) => {
      if (!cancelled) setSeats(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [authToken, kanzleiId]);

  const cells = useMemo(
    () => buildMonthGrid(cursor.year, cursor.monthIndex, today),
    [cursor.monthIndex, cursor.year, today],
  );
  const heading = formatMonthHeading(cursor.year, cursor.monthIndex);
  const dayEvents = useMemo(() => eventsOnDate(events, selectedIso), [events, selectedIso]);
  const dayFristen = useMemo(
    () => fristen.filter((f) => fristTouchesIsoDate(f, selectedIso)),
    [fristen, selectedIso],
  );
  const dayAgenda = useMemo(
    () => buildDayAgenda(selectedIso, events, fristen),
    [events, fristen, selectedIso],
  );
  const hourRows = useMemo(() => buildDayHourRows(selectedIso, events), [events, selectedIso]);
  const weekIsos = useMemo(() => calendarWeekIsos(selectedIso), [selectedIso]);
  const weekHours = useMemo(() => {
    const rows: number[] = [];
    for (let hour = DAY_HOUR_START; hour < DAY_HOUR_END; hour += 1) rows.push(hour);
    return rows;
  }, []);
  const untimedEvents = useMemo(() => dayEvents.filter((ev) => !ev.time), [dayEvents]);
  const otherSeats = useMemo(
    () => seats.filter((s) => s.id && s.id !== customerId),
    [customerId, seats],
  );
  const searchResult = useMemo(
    () =>
      searchCalendarEvents(searchQuery, events, {
        seatId: customerId,
        kanzleiId,
        visibleAkteIds: akten.map((a) => a.id),
      }),
    [akten, customerId, events, kanzleiId, searchQuery],
  );

  const syncCursorToIso = (iso: string) => {
    const d = parseIsoDateLocal(iso);
    if (!d) return;
    setCursor({ year: d.getFullYear(), monthIndex: d.getMonth() });
  };

  const goMonth = (delta: number) => {
    setCursor((prev) => addCalendarMonths(prev.year, prev.monthIndex, delta));
  };

  const goToday = () => {
    setCursor({ year: today.getFullYear(), monthIndex: today.getMonth() });
    setSelectedIso(todayIso);
    setDateText(todayIso);
    if (surface === 'day' || surface === 'week') {
      setPopoverOpen(false);
    }
  };

  const resetForm = (iso: string, ev?: PortalCalendarEvent | null) => {
    setSelectedIso(iso);
    setDateText(ev?.date || iso);
    setTimeText(ev?.time || '');
    setReminderMin(ev ? readCalendarReminderOffset(customerId, ev.id) : 30);
    setTitle(ev?.title || '');
    setAkteId(ev?.akteId || defaultAkteId || '');
    setAttendeeIds((ev?.attendeeIds || []).filter((id) => id && id !== customerId));
    setEditingId(ev?.id || null);
    setFormError('');
    setFormHint('');
  };

  const openDayView = (iso: string, inMonth: boolean) => {
    const opened = applyMonthDayClick(iso);
    if (!opened.selectedIso || !opened.dayViewVisible) return;
    resetForm(opened.selectedIso);
    if (!inMonth) syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    setPopoverOpen(opened.createPopoverOpen);
  };

  const backToMonth = () => {
    closePopover();
    setSurface('month');
    syncCursorToIso(selectedIso);
  };

  const goDay = (delta: number) => {
    const next = addCalendarDays(selectedIso, delta);
    if (!next) return;
    resetForm(next);
    syncCursorToIso(next);
    setPopoverOpen(false);
  };

  const placePopoverFromEl = (el: HTMLElement | null) => {
    const anchor = el?.getBoundingClientRect();
    if (anchor) {
      setPopoverPos(
        placeSharePopover(anchor, { width: window.innerWidth, height: window.innerHeight }, {
          width: DAY_POPOVER_WIDTH_PX,
          height: DAY_POPOVER_EST_HEIGHT_PX,
        }),
      );
    }
  };

  const dropTargetFromPointer = useCallback((clientX: number, clientY: number) => {
    const root = hoursRef.current;
    if (!root) return null;
    const nodes = [...root.querySelectorAll<HTMLElement>('[data-kalender-drop]')];
    const hit = nodes.find((el) => {
      const r = el.getBoundingClientRect();
      return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
    });
    if (!hit) return null;
    const hour = Number(hit.getAttribute('data-hour'));
    const iso = hit.getAttribute('data-day-iso') || '';
    if (!Number.isFinite(hour) || !iso) return null;
    const rect = hit.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientY - rect.top) / Math.max(1, rect.height)));
    const raw = hour * 60 + ratio * 60;
    const target = calendarDropTarget(iso, raw);
    if (!target.date || !target.time) return null;
    const minute = Number(target.time.slice(3, 5));
    const rootRect = root.getBoundingClientRect();
    const quarterTop = rect.top + (minute / 60) * rect.height;
    const quarterHeight = rect.height / 4;
    return {
      iso: target.date,
      time: target.time,
      top: quarterTop - rootRect.top + root.scrollTop,
      left: rect.left - rootRect.left + root.scrollLeft,
      width: rect.width,
      height: quarterHeight,
      viewLeft: rect.left,
      viewTop: quarterTop,
    };
  }, []);

  const moveEventByPointer = useCallback(
    async (ev: PortalCalendarEvent, previewTime: string, previewDate: string) => {
      const origin = calendarTimeRange(ev.time).start;
      if (!origin) {
        setDragPreview(null);
        return;
      }
      const [oh, om] = origin.split(':').map(Number);
      const [th, tm] = previewTime.split(':').map(Number);
      const nextTime = shiftCalendarTime(ev.time, th * 60 + tm - (oh * 60 + om));
      const nextDate = previewDate || ev.date;
      if (!nextTime || (nextTime === origin && nextDate === ev.date)) {
        setDragPreview(null);
        return;
      }
      let snapshot: PortalCalendarEvent[] = [];
      setEvents((prev) => {
        snapshot = prev;
        return prev.map((rowEv) => (rowEv.id === ev.id ? { ...rowEv, time: nextTime, date: nextDate } : rowEv));
      });
      try {
        await saveCalendarEventToPb({
          input: {
            id: ev.id,
            title: ev.title,
            date: nextDate,
            time: nextTime,
            akteId: ev.akteId,
            akteTitle: ev.akteTitle,
            attendeeIds: ev.attendeeIds,
          },
          ctx: writeCtx,
          authToken,
        });
        await refreshEvents();
      } catch {
        setEvents(snapshot);
      } finally {
        setDragPreview(null);
      }
    },
    [authToken, refreshEvents, writeCtx],
  );

  const resizeEventByPointer = useCallback(
    async (ev: PortalCalendarEvent, nextTime: string) => {
      if (!nextTime || nextTime === String(ev.time || '').trim()) {
        setDragPreview(null);
        return;
      }
      let snapshot: PortalCalendarEvent[] = [];
      setEvents((prev) => {
        snapshot = prev;
        return prev.map((rowEv) => (rowEv.id === ev.id ? { ...rowEv, time: nextTime } : rowEv));
      });
      try {
        await saveCalendarEventToPb({
          input: {
            id: ev.id,
            title: ev.title,
            date: ev.date,
            time: nextTime,
            akteId: ev.akteId,
            akteTitle: ev.akteTitle,
            attendeeIds: ev.attendeeIds,
          },
          ctx: writeCtx,
          authToken,
        });
        await refreshEvents();
      } catch {
        setEvents(snapshot);
      } finally {
        setDragPreview(null);
      }
    },
    [authToken, refreshEvents, writeCtx],
  );

  const beginEventDrag = (
    ev: PortalCalendarEvent,
    event: React.PointerEvent<HTMLElement>,
    mode: 'move' | 'start' | 'end' = 'move',
  ) => {
    if (event.button !== 0 || !calendarTimeRange(ev.time).start) return;
    if (mode !== 'move') event.stopPropagation();
    const box = (event.currentTarget.closest('[data-kalender-event-frame]') as HTMLElement | null)
      ?.getBoundingClientRect() || event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      id: ev.id,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      previewTime: null,
      previewDate: ev.date,
      previewRange: null,
      originLeft: box.left,
      originTop: box.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveEventDrag = (ev: PortalCalendarEvent, event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== ev.id) return;
    if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <= 4) return;
    drag.moved = true;
    const drop = dropTargetFromPointer(event.clientX, event.clientY);
    if (!drop) return;
    if (drag.mode === 'move') {
      drag.previewTime = drop.time;
      drag.previewDate = drop.iso;
      drag.previewRange = null;
      const translateX = drop.viewLeft - drag.originLeft;
      const translateY = drop.viewTop - drag.originTop;
      setDragPreview((prev) =>
        prev && prev.id === ev.id && prev.mode === 'move' && prev.time === drop.time && prev.date === drop.iso
          ? prev
          : {
              id: ev.id,
              mode: 'move',
              time: drop.time,
              date: drop.iso,
              range: '',
              translateX,
              translateY,
              top: drop.top,
              left: drop.left,
              width: drop.width,
              height: drop.height,
            },
      );
      return;
    }
    const range = resizeCalendarEdge(ev.time, drag.mode, drop.time);
    if (!range) return;
    drag.previewTime = drop.time;
    drag.previewDate = ev.date;
    drag.previewRange = range;
    setDragPreview((prev) =>
      prev && prev.id === ev.id && prev.mode === drag.mode && prev.range === range && prev.time === drop.time
        ? prev
        : {
            id: ev.id,
            mode: drag.mode,
            time: drop.time,
            date: ev.date,
            range,
            translateX: 0,
            translateY: 0,
            top: drop.top,
            left: drop.left,
            width: drop.width,
            height: drop.height,
          },
    );
  };

  const endEventDrag = (ev: PortalCalendarEvent, event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== ev.id) return;
    dragRef.current = null;
    if (!drag.moved || !drag.previewTime) {
      setDragPreview(null);
      const frame = event.currentTarget.closest('[data-kalender-event-frame]') as HTMLElement | null;
      openCreatePopover({ ev, anchor: frame || event.currentTarget });
      return;
    }
    if (drag.mode === 'move' && drag.previewDate) {
      void moveEventByPointer(ev, drag.previewTime, drag.previewDate);
      return;
    }
    if (drag.previewRange) {
      void resizeEventByPointer(ev, drag.previewRange);
      return;
    }
    setDragPreview(null);
  };

  const openCreatePopover = (opts?: { time?: string; ev?: PortalCalendarEvent | null; anchor?: HTMLElement | null; date?: string }) => {
    resetForm(opts?.date || selectedIso, opts?.ev || null);
    if (!opts?.ev && opts?.time) setTimeText(opts.time);
    placePopoverFromEl(opts?.anchor || createTriggerRef.current);
    setPopoverOpen(true);
  };

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError('');
      setFormHint('');
      const date = parseFlexibleDate(dateText);
      const rawTime = timeText.trim();
      let time = '';
      if (rawTime) {
        const singleTime = parseFlexibleTime(rawTime);
        if (singleTime) time = singleTime;
        else if (isValidCalendarStoredTime(rawTime)) time = rawTime;
        else {
          setFormError('Uhrzeit ungültig — Tipp HH:MM oder Picker nutzen.');
          return;
        }
      }
      if (!date) {
        setFormError('Datum ungültig.');
        return;
      }
      const akte = akten.find((a) => a.id === akteId);
      const prevEvent = editingId ? events.find((row) => row.id === editingId) : null;
      const prevAttendees = new Set(
        (prevEvent?.attendeeIds || []).map((id) => sanitizeKalenderSeatId(id)).filter(Boolean),
      );
      const nextAttendees = new Set(
        attendeeIds.map((id) => sanitizeKalenderSeatId(id)).filter((id) => id && id !== customerId),
      );
      setBusy(true);
      try {
        const saved = await saveCalendarEventToPb({
          input: {
            id: editingId || undefined,
            title,
            date,
            time,
            akteId: akte?.id,
            akteTitle: akte?.title,
            attendeeIds,
          },
          ctx: writeCtx,
          authToken,
        });
        const eventId = sanitizeKalenderSeatId(saved?.id || editingId || '');
        if (eventId) writeCalendarReminderOffset(customerId, eventId, reminderMin);
        const actor = sitzGlockeActorFromSeat(seats.find((s) => s.id === customerId) || null);
        if (eventId) {
          for (const targetId of nextAttendees) {
            if (prevAttendees.has(targetId)) continue;
            noteSitzGlockePortalAction({
              targetSeatId: targetId,
              actorSeatId: customerId,
              actorName: actor?.name,
              actorRole: actor?.role,
              source: 'termin',
              refId: eventId,
              kind: 'added',
            });
          }
          for (const targetId of prevAttendees) {
            if (nextAttendees.has(targetId)) continue;
            noteSitzGlockePortalAction({
              targetSeatId: targetId,
              actorSeatId: customerId,
              actorName: actor?.name,
              actorRole: actor?.role,
              source: 'termin',
              refId: eventId,
              kind: 'removed',
            });
          }
        }
        await refreshEvents();
        setTitle('');
        setTimeText('');
        setAttendeeIds([]);
        setEditingId(null);
        setDateText(date);
        setSelectedIso(date);
        syncCursorToIso(date);
        setPopoverOpen(false);
      } catch (err) {
        setFormError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      } finally {
        setBusy(false);
      }
    },
    [
      akteId,
      akten,
      attendeeIds,
      reminderMin,
      authToken,
      customerId,
      dateText,
      editingId,
      events,
      refreshEvents,
      seats,
      timeText,
      title,
      writeCtx,
    ],
  );

  const handleDelete = async (ev: PortalCalendarEvent) => {
    setFormError('');
    setBusy(true);
    try {
      await deleteCalendarEventFromPb({ event: ev, ctx: writeCtx, authToken });
      const actor = sitzGlockeActorFromSeat(seats.find((s) => s.id === customerId) || null);
      const eventId = sanitizeKalenderSeatId(ev.id);
      for (const raw of ev.attendeeIds || []) {
        const targetId = sanitizeKalenderSeatId(raw);
        if (!targetId || targetId === customerId || !eventId) continue;
        noteSitzGlockePortalAction({
          targetSeatId: targetId,
          actorSeatId: customerId,
          actorName: actor?.name,
          actorRole: actor?.role,
          source: 'termin',
          refId: eventId,
          kind: 'removed',
        });
      }
      await refreshEvents();
      if (editingId === ev.id) {
        resetForm(selectedIso);
        setPopoverOpen(false);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const toggleAttendee = (id: string) => {
    setAttendeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSearchDateJump = (iso: string) => {
    const opened = applyCalendarSearchDateJump(iso);
    if (!opened.selectedIso || !opened.dayViewVisible) return;
    resetForm(opened.selectedIso);
    syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    setPopoverOpen(false);
  };

  const glockeTerminRef = useRef('');
  useEffect(() => {
    const openTermin = (refId: string) => {
      if (!refId || glockeTerminRef.current === refId) return;
      const ev = events.find((row) => row.id === refId);
      if (!ev) return;
      glockeTerminRef.current = refId;
      resetForm(ev.date, ev);
      syncCursorToIso(ev.date);
      setSurface('day');
      placePopoverFromEl(createTriggerRef.current);
      setPopoverOpen(true);
    };
    const pending = peekGlockeSprung();
    if (pending?.source === 'termin') openTermin(pending.refId);
    const onSprung = (event: Event) => {
      const detail = (event as CustomEvent<GlockeSprung>).detail;
      if (!detail || detail.source !== 'termin') return;
      glockeTerminRef.current = '';
      openTermin(detail.refId);
    };
    window.addEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
    return () => window.removeEventListener(GLOCKE_SPRUNG_EVENT, onSprung);
  }, [events]);

  const handleSearchHitClick = (hit: CalendarSearchHit, anchor?: HTMLElement | null) => {
    const opened = applyCalendarSearchHitClick(hit);
    const ev = events.find((row) => row.id === hit.id) || null;
    resetForm(opened.selectedIso, ev);
    syncCursorToIso(opened.selectedIso);
    setSurface(opened.surface);
    if (ev) {
      placePopoverFromEl(anchor || createTriggerRef.current);
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  };

  const pickerDate = parseFlexibleDate(dateText) || '';
  const pickerTime = parseFlexibleTime(timeText) || '';

  const renderSearchControls = () => (
    <div
      className="flex-1 min-w-[14rem] max-w-xl flex items-stretch gap-1.5"
      data-testid="portal-kalender-search"
      data-kalender-search-class={PORTAL_KALENDER_SEARCH_CLASS}
    >
      <label className="relative flex-1 min-w-0">
        <span className="sr-only">Kalender durchsuchen</span>
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none"
          aria-hidden
        />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (searchResult.mode === 'date' && searchResult.dateIso) {
              e.preventDefault();
              handleSearchDateJump(searchResult.dateIso);
              return;
            }
            if (searchResult.mode === 'keyword' && searchResult.hits.length === 1) {
              e.preventDefault();
              handleSearchHitClick(searchResult.hits[0]);
            }
          }}
          placeholder="Titel, Stichwort oder Datum"
          className={`${KALENDER_FIELD} pl-8 pr-2 py-1.5`}
          autoComplete="off"
          data-testid="portal-kalender-search-input"
        />
      </label>
      <label className="block w-[9.75rem] flex-shrink-0">
        <span className="sr-only">Datum suchen</span>
        <input
          type="date"
          value={searchResult.mode === 'date' ? searchResult.dateIso : ''}
          onChange={(e) => {
            const iso = e.target.value;
            setSearchQuery(iso);
            if (iso) handleSearchDateJump(iso);
          }}
          className={`${KALENDER_FIELD} px-2 py-1.5`}
          data-testid="portal-kalender-search-date"
          aria-label="Datum suchen"
        />
      </label>
    </div>
  );

  const renderSearchResults = () => {
    if (searchResult.mode === 'idle') return null;
    return (
      <div
        className={`flex-shrink-0 mb-2 p-2 space-y-1 max-h-36 overflow-y-auto ${KALENDER_EDGE}`}
        data-testid="portal-kalender-search-results"
        data-kalender-search-mode={searchResult.mode}
      >
        {searchResult.emptyVisible ? (
          <p
            className="text-xs text-slate-400 px-1"
            data-testid="portal-kalender-search-empty"
            role="status"
          >
            {CALENDAR_SEARCH_EMPTY_LABEL}
          </p>
        ) : null}
        {searchResult.hits.map((hit) => (
          <button
            key={hit.id}
            type="button"
            data-testid="portal-kalender-search-hit"
            onClick={(e) => handleSearchHitClick(hit, e.currentTarget)}
            className={`w-full text-left rounded-md border px-2 py-1 ${tileClass('event', hit.multiSeat)}`}
          >
            <p className="text-sm text-slate-100 truncate">{hit.title}</p>
            <p className="text-[11px] text-slate-500 truncate">
              {hit.time ? `${hit.time} · ` : ''}
              {formatLongDeDate(hit.date)}
              {hit.akteTitle ? ` · ${hit.akteTitle}` : ''}
            </p>
          </button>
        ))}
        {searchResult.jumpToDay && searchResult.dateIso ? (
          <button
            type="button"
            data-testid="portal-kalender-search-jump-day"
            onClick={() => handleSearchDateJump(searchResult.dateIso)}
            className={KALENDER_ICON}
            aria-label="Tag öffnen"
            title="Tag öffnen"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={`w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-hidden gap-2 px-3 py-3 ${KALENDER_GROUND} ${kalenderLight}`}
      data-testid="portal-kalender-pane"
      data-portal-kalender="1"
      data-kalender-surface={surface}
      data-kalender-search-class={PORTAL_KALENDER_SEARCH_CLASS}
    >
      <header className={`flex-shrink-0 px-3.5 py-3 ${KALENDER_EDGE}`}>
        <h1 className="m-0 text-sm font-semibold text-white">Kalender</h1>
        <p className="m-0 mt-0.5 text-[11px] text-slate-500">
          Monat als Übersicht. Suche nach Titel oder Datum. Tag öffnet den Tag — Termine über Plus oder freien Slot.
        </p>
      </header>

      {surface === 'month' ? (
        <section
          ref={surfaceRef}
          className={`${KALENDER_EDGE} flex-1 min-h-0 flex flex-col p-3 sm:p-4`}
          aria-label="Monatsansicht"
          data-testid="portal-kalender-month-view"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            <h2 className="text-white text-base font-semibold tracking-tight min-w-0 sm:min-w-[8rem]">
              {heading}
            </h2>
            {renderSearchControls()}
            <button
              type="button"
              onClick={goToday}
              className={KALENDER_BTN}
            >
              Heute
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goMonth(-1)}
                className={KALENDER_ICON}
                aria-label="Vorheriger Monat"
                title="Vorheriger Monat"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => goMonth(1)}
                className={KALENDER_ICON}
                aria-label="Nächster Monat"
                title="Nächster Monat"
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>
          </div>
          {renderSearchResults()}

          <div className="grid grid-cols-7 gap-0.5 flex-shrink-0 mb-1">
            {WEEKDAY_LABELS_DE.map((label) => (
              <div key={label} className="text-[11px] font-medium text-slate-500 text-center pb-1">
                {label}
              </div>
            ))}
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-7 grid-rows-6 gap-0.5">
            {cells.map((cell) => {
              const packed = buildDayTiles(cell.iso, events, fristen);
              const selected = cell.iso === selectedIso;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  onClick={() => openDayView(cell.iso, cell.inMonth)}
                  aria-pressed={selected}
                  aria-label={`${formatLongDeDate(cell.iso)}${
                    packed.tiles.length
                      ? `, ${packed.tiles.map((t) => t.title).join(', ')}`
                      : ''
                  }`}
                  data-testid={`kalender-day-${cell.iso}`}
                  className={`portal-kalender-month-day min-h-0 h-full rounded-lg border text-left px-1 py-1 flex flex-col overflow-hidden transition-colors ${
                    selected
                      ? 'border-violet-400/70 bg-violet-600/15'
                      : cell.isToday
                        ? 'portal-kalender-month-today border-violet-400/50 bg-[#07070a]'
                        : 'border-violet-400/20 bg-[#07070a]'
                  } ${cell.inMonth ? '' : 'opacity-40'}`}
                >
                  <span
                    className={`inline-flex items-center justify-center min-w-[1.35rem] h-5 text-xs font-medium rounded-full flex-shrink-0 ${
                      cell.isToday
                        ? 'bg-violet-600 text-white px-1.5'
                        : cell.isWeekend
                          ? 'text-slate-500'
                          : 'text-slate-200'
                    }`}
                  >
                    {cell.day}
                  </span>
                  <span className="mt-0.5 flex flex-col gap-0.5 min-h-0 overflow-hidden">
                    {packed.tiles.map((tile) => {
                      const reminderOn =
                        tile.kind === 'event' &&
                        calendarReminderMarkVisible(readCalendarReminderOffset(customerId, tile.id));
                      return (
                      <span
                        key={tile.id}
                        data-kalender-tile={tile.kind === 'frist' ? 'frist' : tile.multiSeat ? 'multi' : 'single'}
                        data-testid={tile.kind === 'event' ? 'kalender-event-tile' : 'kalender-frist-tile'}
                        data-kalender-month-reminder={reminderOn ? '1' : '0'}
                        className={`flex items-center gap-0.5 min-w-0 text-[10px] leading-tight px-1 py-0.5 rounded border ${tileClass(
                          tile.kind,
                          tile.multiSeat,
                        )}`}
                      >
                        <span className="truncate">{tile.title}</span>
                        {reminderOn ? (
                          <Bell
                            className="portal-kalender-reminder-mark shrink-0 w-2.5 h-2.5"
                            aria-hidden
                          />
                        ) : null}
                      </span>
                      );
                    })}
                    {packed.hidden > 0 ? (
                      <span className="text-[10px] text-slate-500 px-1">+{packed.hidden}</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500 flex-shrink-0">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm border border-violet-400/50 bg-[#07070a]" aria-hidden /> Ein Sitz
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-violet-400/80" aria-hidden /> Mehrere Sitze
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-amber-400/80" aria-hidden /> Frist
            </span>
            {eventsLoading ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden /> Termine…
              </span>
            ) : null}
          </p>
          {loadError ? (
            <p className="mt-1 text-[11px] text-amber-300/90 flex-shrink-0" role="status">
              {loadError}
            </p>
          ) : null}
        </section>
      ) : surface === 'week' ? (
        <section
          ref={surfaceRef}
          className={`${KALENDER_EDGE} flex-1 min-h-0 flex flex-col p-3 sm:p-4`}
          aria-label="Wochenansicht"
          data-testid="portal-kalender-week-view"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            <button
              type="button"
              onClick={backToMonth}
              className={KALENDER_ICON}
              aria-label="Monat"
              title="Monat"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden />
            </button>
            <h2 className="text-white text-base font-semibold tracking-tight min-w-0">
              {formatMonthHeading(cursor.year, cursor.monthIndex)}
            </h2>
            {renderSearchControls()}
            <button type="button" onClick={goToday} className={KALENDER_BTN}>
              Heute
            </button>
            <div className="flex items-center gap-1" role="group" aria-label="Ansicht">
              <button
                type="button"
                data-testid="portal-kalender-view-tag"
                aria-pressed={false}
                onClick={() => setSurface('day')}
                className={KALENDER_VIEW}
              >
                Tag
              </button>
              <button
                type="button"
                data-testid="portal-kalender-view-woche"
                aria-pressed
                onClick={() => setSurface('week')}
                className={KALENDER_BTN}
              >
                Woche
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => goDay(-7)} className={KALENDER_ICON} aria-label="Vorherige Woche" title="Vorherige Woche">
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <button type="button" onClick={() => goDay(7)} className={KALENDER_ICON} aria-label="Nächste Woche" title="Nächste Woche">
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>
            <button
              ref={createTriggerRef}
              type="button"
              onClick={() => {
                if (popoverOpen && !editingId) {
                  closePopover();
                  return;
                }
                openCreatePopover({ anchor: createTriggerRef.current });
              }}
              className="portal-kalender-toolbar relative h-7 w-7 shrink-0 p-0"
              aria-label="Termin anlegen"
              aria-expanded={popoverOpen && !editingId}
              title={popoverOpen && !editingId ? 'Schließen' : neuShortcut}
            >
              <KalenderCube
                open={popoverOpen && !editingId}
                front={<Plus className="w-3.5 h-3.5" aria-hidden />}
                back={<X className="w-3.5 h-3.5" aria-hidden />}
              />
            </button>
          </div>
          {renderSearchResults()}
          <div
            ref={hoursRef}
            className="portal-kalender-hours relative flex-1 min-h-0 overflow-auto"
            data-kalender-hours-fit="1"
          >
            {dragPreview ? (
              <div
                className="portal-kalender-drag-preview pointer-events-none absolute overflow-hidden"
                data-testid="portal-kalender-drag-preview"
                data-preview-time={dragPreview.time}
                style={{
                  top: dragPreview.top,
                  left: dragPreview.left,
                  width: dragPreview.width,
                  height: dragPreview.height,
                }}
              >
                <span className="portal-kalender-preview-time">{dragPreview.time}</span>
              </div>
            ) : null}
            <div
              className="grid min-h-full"
              style={{ gridTemplateColumns: '3.25rem repeat(7, minmax(5.5rem, 1fr))' }}
            >
              <div className="portal-kalender-time-label" />
              {weekIsos.map((iso) => {
                const parsed = parseIsoDateLocal(iso);
                const label = parsed
                  ? `${WEEKDAY_LABELS_DE[mondayIndexFromJsDay(parsed.getDay())]} ${parsed.getDate()}`
                  : iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    data-testid="portal-kalender-week-day"
                    data-day-iso={iso}
                    className={`portal-kalender-week-day text-[11px] text-slate-300 text-left px-1 py-1 truncate ${iso === selectedIso ? 'portal-kalender-week-day-on' : ''}`}
                    onClick={() => {
                      resetForm(iso);
                      setSurface('day');
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              <div className="portal-kalender-time-label" />
              {weekIsos.map((iso) => {
                const dayUntimed = eventsOnDate(events, iso).filter((ev) => !ev.time);
                const dayFristenHere = fristen.filter((frist) => fristTouchesIsoDate(frist, iso));
                if (dayUntimed.length === 0 && dayFristenHere.length === 0) {
                  return <div key={`all-${iso}`} className="min-h-[0.25rem]" />;
                }
                return (
                  <div key={`all-${iso}`} className="min-w-0 px-0.5 py-0.5 space-y-0.5">
                    {dayFristenHere.map((frist) => (
                      <p key={frist.id} className={`truncate rounded-md border px-1 text-[10px] ${tileClass('frist')}`}>
                        {fristLabelOnDate(frist, iso)}
                      </p>
                    ))}
                    {dayUntimed.map((ev) => (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={(e) => openCreatePopover({ ev, date: iso, anchor: e.currentTarget })}
                        className={`portal-kalender-event portal-kalender-event-hover w-full truncate rounded-md border px-1 text-left text-[10px] ${tileClass('event', eventAffectsMultipleSeats(ev))}`}
                      >
                        {ev.title}
                      </button>
                    ))}
                  </div>
                );
              })}
              {weekHours.map((hour) => (
                <React.Fragment key={hour}>
                  <div
                    className="portal-kalender-time-label portal-kalender-hour-row text-[11px] text-slate-500 pt-1 border-t border-violet-400/15"
                    data-hour={hour}
                  >
                    {formatHourLabel(hour)}
                  </div>
                  {weekIsos.map((iso) => {
                    const cellEvents = eventsOnDate(events, iso).filter((ev) => eventStartsInHour(ev.time, hour));
                    const lanes = assignCalendarEventLanes(cellEvents);
                    return (
                      <div
                        key={`${iso}-${hour}`}
                        data-kalender-drop=""
                        data-day-iso={iso}
                        data-hour={hour}
                        className="portal-kalender-hour-row relative min-w-0 border-t border-l border-violet-400/15"
                        style={{ minHeight: CALENDAR_HOUR_MIN_PX }}
                      >
                        <div className="portal-kalender-hour-empty absolute inset-0 grid grid-rows-4">
                          {[0, 15, 30, 45].map((minute) => {
                            const clock = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                            return (
                              <button
                                key={minute}
                                type="button"
                                onClick={(e) => openCreatePopover({ time: clock, date: iso, anchor: e.currentTarget })}
                                className="portal-kalender-quarter portal-kalender-quarter-hover min-h-0 w-full border-0 bg-transparent"
                                aria-label={`Termin um ${clock} anlegen`}
                              />
                            );
                          })}
                        </div>
                        {lanes.items.map((ev) => (
                          <KalenderTimedEvent
                            key={ev.id}
                            ev={ev}
                            lane={ev.lane}
                            laneCount={lanes.count}
                            multi={eventAffectsMultipleSeats(ev)}
                            reminderOn={calendarReminderMarkVisible(readCalendarReminderOffset(customerId, ev.id))}
                            dragging={dragPreview?.id === ev.id ? dragPreview : null}
                            onBegin={beginEventDrag}
                            onMove={moveEventDrag}
                            onEnd={endEventDrag}
                          />
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section
          ref={surfaceRef}
          className={`${KALENDER_EDGE} flex-1 min-h-0 flex flex-col p-3 sm:p-4`}
          aria-label={`Tagesansicht ${formatLongDeDate(selectedIso)}`}
          data-testid="portal-kalender-day-view"
          data-kalender-class={PORTAL_KALENDER_DAY_CLASS}
          data-day-iso={selectedIso}
        >
          <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
            <button
              type="button"
              onClick={backToMonth}
              className={KALENDER_ICON}
              data-testid="portal-kalender-back-month"
              aria-label="Monat"
              title="Monat"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden />
            </button>
            <h2 className="text-white text-base font-semibold tracking-tight min-w-0 sm:min-w-[8rem] capitalize">
              {formatLongDeDate(selectedIso)}
            </h2>
            {renderSearchControls()}
            <button
              type="button"
              onClick={goToday}
              className={KALENDER_BTN}
            >
              Heute
            </button>
            <div className="flex items-center gap-1" role="group" aria-label="Ansicht">
              <button
                type="button"
                data-testid="portal-kalender-view-tag"
                aria-pressed={surface === 'day'}
                onClick={() => setSurface('day')}
                className={surface === 'day' ? KALENDER_BTN : KALENDER_VIEW}
              >
                Tag
              </button>
              <button
                type="button"
                data-testid="portal-kalender-view-woche"
                aria-pressed={surface === 'week'}
                onClick={() => setSurface('week')}
                className={surface === 'week' ? KALENDER_BTN : KALENDER_VIEW}
              >
                Woche
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goDay(-1)}
                className={KALENDER_ICON}
                aria-label="Vorheriger Tag"
                title="Vorheriger Tag"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => goDay(1)}
                className={KALENDER_ICON}
                aria-label="Nächster Tag"
                title="Nächster Tag"
              >
                <ChevronRight className="w-4 h-4" aria-hidden />
              </button>
            </div>
            <button
              ref={createTriggerRef}
              type="button"
              onClick={() => {
                if (popoverOpen && !editingId) {
                  closePopover();
                  return;
                }
                openCreatePopover({ anchor: createTriggerRef.current });
              }}
              className="portal-kalender-toolbar relative h-7 w-7 shrink-0 p-0"
              data-testid="portal-kalender-day-create"
              data-portal-shortcut="neu"
              aria-keyshortcuts={neuShortcut}
              aria-label="Termin anlegen"
              aria-expanded={popoverOpen && !editingId}
              title={popoverOpen && !editingId ? 'Schließen' : neuShortcut}
            >
              <KalenderCube
                open={popoverOpen && !editingId}
                front={<Plus className="w-3.5 h-3.5" aria-hidden />}
                back={<X className="w-3.5 h-3.5" aria-hidden />}
              />
            </button>
            <PortalShortcutHint id="neu" className="border-violet-300/40 text-violet-200/80" />
          </div>
          {renderSearchResults()}

          <div
            className="flex-1 min-h-0 flex flex-col overflow-hidden"
            data-testid="portal-kalender-day-list"
          >
            {fristenLoading || eventsLoading ? (
              <p className="flex items-center gap-2 text-xs text-slate-500 flex-shrink-0 mb-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                Einträge werden geladen…
              </p>
            ) : null}

            <div className="flex-shrink-0 max-h-[28%] overflow-y-auto space-y-1.5 pb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Ganztägig / ohne Zeit</p>
              {dayFristen.length === 0 && untimedEvents.length === 0 ? (
                <p className="text-xs text-slate-500 px-1">Keine ganztägigen Einträge.</p>
              ) : null}
              {dayAgenda.length === 0 ? (
                <p className="text-[11px] text-slate-500 px-1" data-testid="portal-kalender-day-empty">
                  Keine Einträge — Plus oder freien Slot nutzen.
                </p>
              ) : null}
              {dayFristen.map((frist) => (
                <div
                  key={frist.id}
                  data-testid="portal-kalender-day-entry"
                  data-kalender-day-kind="frist"
                  className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-2.5 py-1.5"
                >
                  <p className="text-[10px] uppercase tracking-wider text-amber-400/90 font-medium">
                    Frist
                  </p>
                  <p className="text-sm text-slate-100 mt-0.5">{fristLabelOnDate(frist, selectedIso)}</p>
                </div>
              ))}
              {untimedEvents.map((ev) => {
                const multi = eventAffectsMultipleSeats(ev);
                return (
                  <button
                    key={ev.id}
                    type="button"
                    data-testid="portal-kalender-day-entry"
                    data-kalender-day-kind="event"
                    onClick={(e) => openCreatePopover({ ev, anchor: e.currentTarget })}
                    className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${tileClass('event', multi)}`}
                  >
                    <p className="text-[10px] uppercase tracking-wider font-medium text-violet-200">
                      Termin{multi ? ' · mehrere Sitze' : ''}
                    </p>
                    <p className="text-sm text-slate-100 mt-0.5">{ev.title}</p>
                    {ev.akteTitle ? (
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate">Akte: {ev.akteTitle}</p>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div
              ref={hoursRef}
              className="portal-kalender-hours relative flex-1 min-h-0 flex flex-col overflow-y-auto border-t border-violet-400/20"
              data-testid="portal-kalender-day-hours"
              data-kalender-hours-fit="1"
            >
              {dragPreview ? (
                <div
                  className="portal-kalender-drag-preview pointer-events-none absolute overflow-hidden"
                  data-testid="portal-kalender-drag-preview"
                  data-preview-time={dragPreview.time}
                  style={{
                    top: dragPreview.top,
                    left: dragPreview.left,
                    width: dragPreview.width,
                    height: dragPreview.height,
                  }}
                >
                  <span className="portal-kalender-preview-time">{dragPreview.time}</span>
                </div>
              ) : null}
              {hourRows.map((row) => (
                <div
                  key={row.hour}
                  data-kalender-hour-row=""
                  data-hour={row.hour}
                  className="portal-kalender-hour-row grid shrink-0 grid-cols-[3.25rem_minmax(0,1fr)] gap-2 border-t border-violet-400/15"
                  style={{ minHeight: CALENDAR_HOUR_MIN_PX }}
                  data-testid={`kalender-hour-${row.hour}`}
                >
                  <button
                    type="button"
                    onClick={(e) =>
                      openCreatePopover({ time: formatHourLabel(row.hour), anchor: e.currentTarget })
                    }
                    className="portal-kalender-time-label text-[11px] text-slate-500 text-left self-start pt-1 hover:text-slate-300"
                    aria-label={`Termin um ${row.label} anlegen`}
                  >
                    {row.label}
                  </button>
                  <div
                    data-kalender-drop=""
                    data-day-iso={selectedIso}
                    data-hour={row.hour}
                    className="portal-kalender-hour-lanes relative min-w-0 h-full"
                  >
                    <div
                      className="portal-kalender-hour-empty absolute inset-0 grid grid-rows-4"
                      data-testid={`kalender-hour-slot-${row.hour}`}
                    >
                      {[0, 15, 30, 45].map((minute) => {
                        const clock = `${String(row.hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                        return (
                          <button
                            key={minute}
                            type="button"
                            onClick={(e) => openCreatePopover({ time: clock, anchor: e.currentTarget })}
                            className="portal-kalender-quarter portal-kalender-quarter-hover min-h-0 w-full border-0 bg-transparent text-left"
                            aria-label={`Termin um ${clock} anlegen`}
                          />
                        );
                      })}
                    </div>
                    {(() => {
                      const lanes = assignCalendarEventLanes(row.events);
                      return lanes.items.map((ev) => (
                        <KalenderTimedEvent
                          key={ev.id}
                          ev={ev}
                          lane={ev.lane}
                          laneCount={lanes.count}
                          multi={eventAffectsMultipleSeats(ev)}
                          reminderOn={calendarReminderMarkVisible(readCalendarReminderOffset(customerId, ev.id))}
                          dragging={dragPreview?.id === ev.id ? dragPreview : null}
                          onBegin={beginEventDrag}
                          onMove={moveEventDrag}
                          onEnd={endEventDrag}
                        />
                      ));
                    })()}
                  </div>
                </div>
              ))}
            </div>

            {loadError ? (
              <p className="text-[11px] text-amber-300/90 flex-shrink-0 mt-1" role="status">
                {loadError}
              </p>
            ) : null}
          </div>
        </section>
      )}

      {popoverOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label={editingId ? 'Termin ändern' : 'Termin anlegen'}
              data-testid="portal-kalender-day-popover"
              data-kalender-popover-fit="viewport-scroll"
              data-popover-dismiss="POPOVER_DISMISS_OUTSIDE"
              className={`portal-kalender-popover fixed z-[85] flex flex-col overflow-hidden rounded-xl border border-violet-400/30 bg-[#07070a] shadow-2xl ${kalenderLight}`}
              style={{
                left: popoverPos.left,
                top: popoverPos.top,
                width: `min(calc(100vw - 1.5rem), ${DAY_POPOVER_WIDTH_PX}px)`,
                maxHeight: `min(calc(100dvh - 1rem), calc(100dvh - ${popoverPos.top}px - 0.75rem))`,
              }}
            >
              <div className="flex items-start gap-2 flex-shrink-0 p-3 pb-2">
                <CalendarDays className="w-4 h-4 text-violet-300 mt-0.5 flex-shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="text-white text-sm font-semibold leading-snug">
                    {editingId ? 'Termin ändern' : 'Termin anlegen'}
                  </h2>
                  <p className="text-slate-500 text-xs mt-0.5 capitalize">
                    {formatLongDeDate(selectedIso)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closePopover}
                  className={KALENDER_ICON}
                  aria-label="Schließen"
                >
                  <X className="w-4 h-4" aria-hidden />
                </button>
              </div>

              <form onSubmit={(e) => void handleSave(e)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 overflow-y-auto space-y-2.5 px-3 pb-2">
                <p className="text-xs font-medium text-slate-300 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" aria-hidden />
                  {editingId ? 'Änderungen speichern' : 'Neuer Termin'}
                </p>
                <label className="block">
                  <span className="sr-only">Titel</span>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    maxLength={160}
                    placeholder="Überschrift"
                    className={`${KALENDER_FIELD} px-3 py-2`}
                  />
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="portal-kalender-datetime">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Datum</span>
                    <input
                      type="date"
                      value={pickerDate}
                      onChange={(e) => setDateText(e.target.value)}
                      required
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                      aria-label="Datum"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Zeit</span>
                    <input
                      type="text"
                      value={timeText}
                      onChange={(e) => setTimeText(e.target.value)}
                      placeholder="HH:MM"
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                    />
                    <input
                      type="time"
                      value={pickerTime}
                      onChange={(e) => setTimeText(e.target.value)}
                      className={`${KALENDER_FIELD} px-3 py-1.5 mt-1`}
                      aria-label="Uhrzeit wählen"
                    />
                  </label>
                </div>
                {akten.length > 0 ? (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Akte (optional)</span>
                    <select
                      value={akteId}
                      onChange={(e) => setAkteId(e.target.value)}
                      className={`${KALENDER_FIELD} px-3 py-2 mt-0.5`}
                    >
                      <option value="">Keine Akte</option>
                      {akten.map((akte) => (
                        <option key={akte.id} value={akte.id}>
                          {akte.title}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <fieldset className="space-y-1" data-testid="portal-kalender-reminder-choice">
                  <legend className="text-[10px] uppercase tracking-wider text-slate-500">Erinnerung</legend>
                  <div className="flex flex-wrap gap-1">
                    {([
                      [15, '15 Minuten'],
                      [30, '30 Minuten'],
                      [60, 'eine Stunde'],
                      [0, 'aus'],
                    ] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={reminderMin === value}
                        onClick={() => setReminderMin(value)}
                        className={reminderMin === value ? KALENDER_BTN : KALENDER_VIEW}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                {otherSeats.length > 0 ? (
                  <fieldset className="space-y-1">
                    <legend className="text-[10px] uppercase tracking-wider text-slate-500">
                      Sitze benachrichtigen
                    </legend>
                    <div className="max-h-28 overflow-y-auto space-y-1">
                      {otherSeats.map((seat) => (
                        <label key={seat.id} className="flex items-center gap-2 text-xs text-slate-300">
                          <input
                            type="checkbox"
                            checked={attendeeIds.includes(seat.id)}
                            onChange={() => toggleAttendee(seat.id)}
                          />
                          <span className="truncate">{portalShareSeatLabel(seat)}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
                {editingId ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-500 truncate">
                      {dayEvents.find((ev) => ev.id === editingId)?.title || 'Termin'}
                    </p>
                    {dayEvents.find((ev) => ev.id === editingId)?.customerId === customerId ? (
                      <button
                        type="button"
                        onClick={() => {
                          const ev = dayEvents.find((row) => row.id === editingId);
                          if (ev) void handleDelete(ev);
                        }}
                        className="p-1 rounded-md text-slate-500 hover:text-red-300 hover:bg-red-500/10"
                        aria-label="Termin löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                ) : null}
                </div>
                {formError ? (
                  <p className="text-xs text-red-400 px-3 pt-2" role="alert">
                    {formError}
                  </p>
                ) : null}
                {formHint ? <p className="text-xs text-slate-500 px-3 pt-2">{formHint}</p> : null}
                <div className="flex gap-2 flex-shrink-0 p-3 pt-2 border-t border-white/[0.06]">
                  <button
                    type="submit"
                    disabled={busy}
                    className={`${KALENDER_BTN} flex-1 justify-center gap-2`}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> : null}
                    Speichern
                  </button>
                  {editingId ? (
                    <button
                      type="button"
                      onClick={() => resetForm(selectedIso)}
                      className={KALENDER_BTN}
                    >
                      Neu
                    </button>
                  ) : null}
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default KalenderView;
